class ImageCache {
  private readonly cache = new Map<string, HTMLImageElement>()

  private readonly loading = new Map<string, Promise<HTMLImageElement>>()

  async loadImage(src: string): Promise<HTMLImageElement> {
    const cachedImage = this.cache.get(src)
    if (cachedImage) {
      return cachedImage
    }

    const loadingPromise = this.loading.get(src)
    if (loadingPromise) {
      return loadingPromise
    }

    const newLoadingPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        this.cache.set(src, img)
        this.loading.delete(src)
        resolve(img)
      }

      img.onerror = (error) => {
        this.loading.delete(src)
        reject(new Error(`Failed to load image: ${src}. ${error}`))
      }

      img.src = src
    })

    this.loading.set(src, newLoadingPromise)
    return newLoadingPromise
  }

  preloadImages(srcs: string[]): Promise<HTMLImageElement[]> {
    return Promise.all(srcs.map((src) => this.loadImage(src)))
  }

  clearCache(): void {
    this.cache.clear()
    this.loading.clear()
  }

  removeFromCache(src: string): void {
    this.cache.delete(src)
    this.loading.delete(src)
  }

  getCacheSize(): number {
    return this.cache.size
  }
}

export const imageCache = new ImageCache()
